import { defaultTravelDetailsFormValues } from "@/D2C/features/travel-quote/redux/travel-quote/travelQuoteSlice";
import { CountrySelect, RadioInput, SelectInput } from "@/shared/components";
import DateInput from "@/shared/components/DateInput";
import { usePosterReducers } from "@/shared/redux/store/useGetData";
import { SharedPersonalAutofillValues } from "@/shared/stepper/PersonalDetailsFormStep";
import type { StepFormComponentProps, StepFormHandle } from "@/shared/stepper/types";
import { travelDetailsSchema } from "@/shared/validation/travelQuoteSchemas";
import { zodResolver } from "@hookform/resolvers/zod";
import { forwardRef, useEffect, useImperativeHandle } from "react";
import { useFieldArray, useForm, useWatch } from "react-hook-form";
import { useTravelDetailsFormEffects } from "../../hooks/useTravelDetailsFormEffects";
import { TravelDetailsFormValues } from "../../redux/type";
import {
  addDays,
  COVER_TYPE_OPTIONS,
  formatIsoDate,
  MAX_TRAVELLERS,
  OUTBOUND_UAE_COVERAGES,
  PLAN_TYPE_OPTIONS,
} from "../../utils/travelDetails";
import { TravelTravellersSection } from "./TravelTravellersSection";

const normalizeOcrDateToIso = (value?: string) => {
  if (!value) {
    return "";
  }

  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return value;
  }

  const slashFormat = value.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (slashFormat) {
    const [, day, month, year] = slashFormat;
    return `${year}-${month}-${day}`;
  }

  const dotFormat = value.match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
  if (dotFormat) {
    const [, day, month, year] = dotFormat;
    return `${year}-${month}-${day}`;
  }

  return value;
};

export const TravelDetailsStep = forwardRef<
  StepFormHandle<TravelDetailsFormValues>,
  StepFormComponentProps<
    TravelDetailsFormValues,
    {
      autofillValues?: SharedPersonalAutofillValues;
      autofillVersion?: number;
      layoutClassName?: string;
      additionalTravellersLayout?: "stacked" | "grid";
      countryFieldsLayout?: "row" | "inline";
      travellerActionButtonsClassName?: string;
      disableSharedPrimaryTravellerAutofill?: boolean;
      livePrimaryTravellerName?: string;
      isReadOnly?: boolean;
    }
  >
>(function TravelDetailsStep(
  {
    defaultValues,
    autofillValues,
    autofillVersion,
    layoutClassName,
    additionalTravellersLayout,
    countryFieldsLayout,
    travellerActionButtonsClassName,
    disableSharedPrimaryTravellerAutofill,
    livePrimaryTravellerName,
    isReadOnly = false,
  },
  ref
) {
  const {
    sharedPersonalDetails,
    masterCoverage: masterCoverageData,
    autofillPersonalDetails,
  } = usePosterReducers();
  const masterCoverage = masterCoverageData?.masterCoverage || [];
  const countryLayout = countryFieldsLayout ?? "row";
  const persistedAutofillValues = autofillPersonalDetails?.autofillData || {};
  const resolvedAutofillValues =
    Object.keys(autofillValues || {}).length > 0 ? autofillValues : persistedAutofillValues;

  const form = useForm<TravelDetailsFormValues>({
    resolver: zodResolver(travelDetailsSchema) as any,
    defaultValues: defaultValues ?? defaultTravelDetailsFormValues,
    mode: "onChange",
    reValidateMode: "onChange",
  });

  const { fields, append, remove, replace } = useFieldArray({
    control: form.control,
    name: "travellers",
  });

  const coverage = useWatch({
    control: form.control,
    name: "coverage",
  });

  const planType = useWatch({
    control: form.control,
    name: "planType",
  });

  const coverType = useWatch({
    control: form.control,
    name: "coverType",
  });

  const startDate = useWatch({
    control: form.control,
    name: "startDate",
  });

  const fromCountry = useWatch({
    control: form.control,
    name: "fromCountry",
  });

  const destination = useWatch({
    control: form.control,
    name: "destination",
  });

  const primaryTravellerName = useWatch({
    control: form.control,
    name: "travellers.0.name",
  });

  const selectedCoverage = masterCoverage.find((item) => String(item.id) === String(coverage));

  const coverageLabel = selectedCoverage?.label || "";

  const isUaeInbound = coverageLabel === "UAE Inbound";

  const isWorldwideCoverage = OUTBOUND_UAE_COVERAGES.has(coverageLabel);

  const masterCoverageOptions = masterCoverage.map((item) => ({
    value: String(item.id),
    label: item.label,
  }));

  const hasSingleCoverageOption = masterCoverageOptions.length === 1;

  const defaultTravellerName = disableSharedPrimaryTravellerAutofill
    ? ""
    : [sharedPersonalDetails.firstName, sharedPersonalDetails.lastName]
        .filter(Boolean)
        .join(" ")
        .trim();

  const { countryOptions, showCountryFields, todayIso } = useTravelDetailsFormEffects({
    defaultValues,
    form,
    defaultTravellerName,
    primaryTravellerName,
    selectedCoverage,
    masterCoverageLength: masterCoverage.length,
    isUaeInbound,
    isWorldwideCoverage,
    coverType,
    fieldsLength: fields.length,
    planType,
    startDate,
    replace,
  });

  useEffect(() => {
    if (!hasSingleCoverageOption) {
      return;
    }

    const onlyCoverage = masterCoverageOptions[0];

    if (!onlyCoverage || String(coverage) === String(onlyCoverage.value)) {
      return;
    }

    form.setValue("coverage", String(onlyCoverage.value), {
      shouldDirty: true,
      shouldValidate: true,
    });
  }, [coverage, form, hasSingleCoverageOption, masterCoverageOptions]);

  useEffect(() => {
    if (!resolvedAutofillValues) return;

    const normalizedStartDate = normalizeOcrDateToIso(resolvedAutofillValues.startDate);
    const normalizedEndDate = normalizeOcrDateToIso(resolvedAutofillValues.endDate);

    if (normalizedStartDate) {
      form.setValue("startDate", normalizedStartDate, {
        shouldDirty: true,
        shouldValidate: true,
      });
    }

    if (normalizedEndDate) {
      form.setValue("endDate", normalizedEndDate, {
        shouldDirty: true,
        shouldValidate: true,
      });
    }

    if (resolvedAutofillValues.start) {
      form.setValue("fromCountry", resolvedAutofillValues.start, {
        shouldDirty: true,
        shouldValidate: true,
      });
    }

    if (resolvedAutofillValues.to) {
      form.setValue("destination", resolvedAutofillValues.to, {
        shouldDirty: true,
        shouldValidate: true,
      });
    }

    if (resolvedAutofillValues.start && resolvedAutofillValues.to) {
      const worldwideCoverage = masterCoverage.find((item) => item.label === "Worldwide");

      if (worldwideCoverage) {
        form.setValue("coverage", String(worldwideCoverage.id), {
          shouldDirty: true,
          shouldValidate: true,
        });
      }
    }
  }, [autofillVersion, form, masterCoverage, resolvedAutofillValues]);

  useEffect(() => {
    if (livePrimaryTravellerName === undefined) {
      return;
    }

    const normalizedName = livePrimaryTravellerName.trim();
    if (form.getValues("travellers.0.name") === normalizedName) {
      return;
    }

    form.setValue("travellers.0.name", normalizedName, {
      shouldDirty: true,
      shouldValidate: true,
    });
  }, [form, livePrimaryTravellerName]);

  useImperativeHandle(ref, () => ({
    submit: async () => {
      if (isReadOnly) {
        return true;
      }

      return form.trigger(undefined, {
        shouldFocus: true,
      });
    },
    getValues: () => form.getValues(),
    coverageLabel,
  }));

  const canAddTraveller =
    (coverType === "group" || coverType === "family") && fields.length < MAX_TRAVELLERS;

  const additionalTravellerLimit = MAX_TRAVELLERS - 1;

  const isAnnualMultiTrip = planType === "annual";

  const maxSingleTripEndDate = startDate
    ? formatIsoDate(addDays(new Date(startDate), 179))
    : undefined;

  const formLayoutClassName = layoutClassName ?? "grid grid-cols-1 gap-4 sm:grid-cols-2";

  return (
    <fieldset disabled={isReadOnly} className="space-y-6 border-0 p-0">
      <div className={formLayoutClassName}>
        <SelectInput
          control={form.control}
          name="coverage"
          label="What Coverage Do You Need?"
          placeholder="Select coverage"
          className="coverageNeed"
          options={masterCoverageOptions}
          searchable
          hideChevron={hasSingleCoverageOption}
          disabled={hasSingleCoverageOption}
          required
        />

        <RadioInput
          control={form.control}
          name="planType"
          className="travelPlanType"
          label="Plan Type"
          direction="column"
          options={[...PLAN_TYPE_OPTIONS]}
        />

        {showCountryFields &&
          (countryLayout === "inline" ? (
            <>
              <CountrySelect
                control={form.control}
                name="fromCountry"
                label="From"
                placeholder="Select departure country"
                coverage={coverageLabel}
                options={countryOptions}
                excludeCountry={destination}
                disabled={isWorldwideCoverage}
                searchable={!isWorldwideCoverage}
                hideChevron={isWorldwideCoverage}
                required
              />

              <CountrySelect
                control={form.control}
                name="destination"
                label="To"
                placeholder="Select destination"
                coverage={coverageLabel}
                options={countryOptions}
                excludeCountry={fromCountry}
                disabled={isUaeInbound}
                searchable={!isUaeInbound}
                hideChevron={isUaeInbound}
                required
              />
            </>
          ) : (
            <div className="col-span-full grid grid-cols-1 gap-4 sm:grid-cols-2">
              <CountrySelect
                control={form.control}
                name="fromCountry"
                label="From"
                placeholder="Select departure country"
                coverage={coverageLabel}
                options={countryOptions}
                excludeCountry={destination}
                disabled={isWorldwideCoverage}
                searchable={!isWorldwideCoverage}
                hideChevron={isWorldwideCoverage}
                required
              />

              <CountrySelect
                control={form.control}
                name="destination"
                label="To"
                placeholder="Select destination"
                coverage={coverageLabel}
                options={countryOptions}
                excludeCountry={fromCountry}
                disabled={isUaeInbound}
                searchable={!isUaeInbound}
                hideChevron={isUaeInbound}
                required
              />
            </div>
          ))}

        <DateInput
          control={form.control}
          name="startDate"
          label="Start Date"
          className="travelStartDate"
          placeholder="Select start date"
          min={todayIso}
          required
          yearOrder="asc"
        />

        <DateInput
          control={form.control}
          name="endDate"
          className="travelEndDate"
          label="End Date"
          placeholder={isAnnualMultiTrip ? "Auto-calculated for annual plan" : "Select end date"}
          min={startDate || todayIso}
          max={isAnnualMultiTrip ? undefined : maxSingleTripEndDate}
          disabled={isAnnualMultiTrip}
          required
          yearOrder="asc"
        />

        <RadioInput
          control={form.control}
          name="coverType"
          label="Cover Type"
          options={[...COVER_TYPE_OPTIONS]}
        />

        <div className="col-span-full">
          <TravelTravellersSection
            control={form.control}
            fields={fields}
            coverType={coverType}
            todayIso={todayIso}
            canAddTraveller={canAddTraveller}
            additionalTravellersLayout={additionalTravellersLayout}
            additionalTravellerLimit={additionalTravellerLimit}
            actionButtonsClassName={travellerActionButtonsClassName}
            onAddTraveller={() =>
              append({
                name: "",
                dob: "",
              })
            }
            onRemoveTraveller={remove}
          />
        </div>
      </div>
    </fieldset>
  );
});
